import React, { useState } from "react";
import {
  Box, Typography, TextField, Button, IconButton, Alert,
  Dialog, DialogContent, DialogActions,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import FavoriteIcon from "@mui/icons-material/Favorite";
import axios from "axios";
import { sanitizeNameInput, isValidName, sanitizePhoneInput, isValidPhone, isValidEmail } from "./validators";

// Same label-above-input "Field" wrapper used by Login.js/Register.js/
// StaffLogin.js — this dialog previously used bare MUI TextFields with a
// floating label instead, which is why it read as a different, older-looking
// form component next to every other form in the app.
function Field({ label, isDark, children }) {
  const sub = isDark ? "rgba(255,255,255,0.55)" : "#475569";
  return (
    <Box sx={{ flex: 1 }}>
      <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.06em", color: sub, textTransform: "uppercase", mb: 0.75 }}>
        {label}
      </Typography>
      {children}
    </Box>
  );
}

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";
const KIT_PRICE = 299;
// Mirrors the backend's PAY_IT_FORWARD_MAX_QTY default — the backend is the
// actual authority (re-validated there on submit), this just keeps the
// stepper's upper bound from looking unlimited in the UI.
export const PAY_IT_FORWARD_MAX_QTY = 25;

const ROSE = "#e11d48";

function Stepper({ value, onChange, min, max, isDark, size = "normal" }) {
  const big = size === "big";
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: big ? 1.5 : 1 }}>
      <IconButton
        onClick={() => onChange(Math.max(min, value - 1))} disabled={value <= min}
        sx={{
          width: big ? 40 : 32, height: big ? 40 : 32,
          border: `1.5px solid ${isDark ? "rgba(255,255,255,0.16)" : "rgba(10,14,39,0.15)"}`,
          color: isDark ? "#e2e8f0" : "#1e293b",
          "&:hover": { borderColor: ROSE, color: ROSE, bgcolor: isDark ? "rgba(225,29,72,0.1)" : "rgba(225,29,72,0.06)" },
        }}
      >
        <RemoveIcon fontSize={big ? "small" : "inherit"} sx={!big ? { fontSize: 16 } : undefined} />
      </IconButton>
      <Typography sx={{
        fontSize: big ? 30 : 20, fontWeight: 800, minWidth: big ? 56 : 32, textAlign: "center",
        color: isDark ? "#f8fafc" : "#0a0e27", fontVariantNumeric: "tabular-nums",
      }}>
        {value}
      </Typography>
      <IconButton
        onClick={() => onChange(Math.min(max, value + 1))} disabled={value >= max}
        sx={{
          width: big ? 40 : 32, height: big ? 40 : 32,
          border: `1.5px solid ${isDark ? "rgba(255,255,255,0.16)" : "rgba(10,14,39,0.15)"}`,
          color: isDark ? "#e2e8f0" : "#1e293b",
          "&:hover": { borderColor: ROSE, color: ROSE, bgcolor: isDark ? "rgba(225,29,72,0.1)" : "rgba(225,29,72,0.06)" },
        }}
      >
        <AddIcon fontSize={big ? "small" : "inherit"} sx={!big ? { fontSize: 16 } : undefined} />
      </IconButton>
    </Box>
  );
}

// The quantity-only stepper embedded as an "extra" inside an actual
// telehealth order (registration wizard's Extras step, and MyPayments'
// Order Telehealth dialog) — bundles N sponsored kits into that SAME
// checkout. No name/email fields here: the parent placing the telehealth
// order already supplies those for their own order.
export function PayItForwardExtra({ quantity, setQuantity, isDark }) {
  const active = quantity > 0;
  return (
    <Box sx={{
      mt: 2, p: 2.5, borderRadius: "16px", position: "relative", overflow: "hidden",
      border: `2px solid ${active ? ROSE : (isDark ? "rgba(255,255,255,0.09)" : "rgba(10,14,39,0.12)")}`,
      background: active
        ? (isDark
          ? "linear-gradient(135deg, rgba(225,29,72,0.14) 0%, rgba(225,29,72,0.04) 100%)"
          : "linear-gradient(135deg, rgba(225,29,72,0.07) 0%, rgba(225,29,72,0.02) 100%)")
        : "transparent",
      transition: "all 0.2s",
    }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
        <Box sx={{
          width: 28, height: 28, borderRadius: "8px", display: "flex", alignItems: "center", justifyContent: "center",
          bgcolor: isDark ? "rgba(225,29,72,0.18)" : "rgba(225,29,72,0.1)",
        }}>
          <FavoriteIcon sx={{ fontSize: 15, color: ROSE }} />
        </Box>
        <Typography sx={{ fontSize: 15, fontWeight: 700, color: isDark ? "#f8fafc" : "#0a0e27" }}>
          Pay It Forward — sponsor a KidSafe test
        </Typography>
      </Box>
      <Typography sx={{ fontSize: 12.5, color: isDark ? "#94a3b8" : "#64748b", mb: 1.75, lineHeight: 1.6 }}>
        Add extra kits (${KIT_PRICE} each) to this same checkout for families who can't afford one. Totally optional.
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}>
        <Stepper value={quantity} onChange={setQuantity} min={0} max={PAY_IT_FORWARD_MAX_QTY} isDark={isDark} />
        {active && (
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: ROSE }}>
            +${(quantity * KIT_PRICE).toLocaleString()} to this checkout
          </Typography>
        )}
      </Box>
    </Box>
  );
}

// Standalone donation flow — no patient account, no login. Used both as its
// own "path card" on the registration landing and as a card on MyPayments.
export function PayItForwardDialog({ open, onClose, isDark }) {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [qty, setQty] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const canSubmit = isValidName(firstName) && isValidName(lastName) && isValidPhone(phone)
    && isValidEmail(email) && qty >= 1 && qty <= PAY_IT_FORWARD_MAX_QTY;
  const cardBg = isDark ? "rgba(17,24,39,0.97)" : "#ffffff";
  // Same recipe as Login.js/Register.js/StaffLogin.js's shared inputSx
  // (rounded 10px, subtle tint, focus glow) — just the accent color swapped
  // to rose to match this flow's branding, everything else identical.
  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "10px",
      bgcolor: isDark ? "rgba(255,255,255,0.03)" : "rgba(10,14,39,0.018)",
      fontSize: 16,
      "& fieldset": { borderColor: isDark ? "rgba(255,255,255,0.09)" : "rgba(10,14,39,0.14)" },
      "&:hover fieldset": { borderColor: isDark ? "rgba(255,255,255,0.22)" : "rgba(10,14,39,0.3)" },
      "&.Mui-focused fieldset": { borderColor: ROSE, borderWidth: "2px" },
      "&.Mui-focused": { boxShadow: "0 0 0 3px rgba(225,29,72,0.08)" },
    },
    "& .MuiOutlinedInput-input": { py: "13px", px: "14px" },
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const res = await axios.post(`${API_BASE}/kidsafe-pay-it-forward/orders`, {
        purchaser_first_name: firstName.trim(), purchaser_last_name: lastName.trim(),
        purchaser_email: email.trim(), purchaser_phone: phone.trim(), quantity: qty,
      });
      window.location.href = res.data.checkout_url;
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to start checkout. Please try again.");
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open} onClose={() => !submitting && onClose()} maxWidth="xs" fullWidth
      PaperProps={{ sx: { borderRadius: "20px", overflow: "hidden", bgcolor: cardBg } }}
    >
      {/* Gradient header — matches the app's login/staff-login card treatment,
          rose-tinted instead of navy/accent to visually mark this as the
          donation flow, not another test-purchase path. */}
      <Box sx={{
        background: `linear-gradient(135deg, #0a0e27 0%, ${ROSE} 140%)`,
        px: 3.5, py: 3, position: "relative", overflow: "hidden",
      }}>
        <Box sx={{ position: "absolute", top: -30, right: -30, width: 120, height: 120, borderRadius: "50%", bgcolor: "rgba(255,255,255,0.06)" }} />
        <Box sx={{
          width: 44, height: 44, borderRadius: "12px", display: "flex", alignItems: "center", justifyContent: "center",
          bgcolor: "rgba(255,255,255,0.14)", mb: 1.5,
        }}>
          <FavoriteIcon sx={{ fontSize: 22, color: "#fff" }} />
        </Box>
        <Typography sx={{ fontSize: 19, fontWeight: 800, color: "#fff", position: "relative" }}>
          Pay It Forward
        </Typography>
        <Typography sx={{ fontSize: 13, color: "rgba(255,255,255,0.8)", mt: 0.25, position: "relative" }}>
          Sponsor a KidSafe test for a family in need
        </Typography>
      </Box>

      <DialogContent sx={{ bgcolor: cardBg, pt: 3 }}>
        <Typography sx={{ fontSize: 13, color: isDark ? "#94a3b8" : "#64748b", mb: 2.5, lineHeight: 1.6 }}>
          ${KIT_PRICE} covers one full KidSafe telehealth kit, kit shipping, and clinician review for a family
          who can't afford one. Choose how many you'd like to sponsor.
        </Typography>
        {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}

        <Box sx={{ display: "flex", gap: 1.5, mb: 2.5 }}>
          <Field label="First name" isDark={isDark}>
            <TextField fullWidth sx={fieldSx} value={firstName} onChange={(e) => setFirstName(sanitizeNameInput(e.target.value))} />
          </Field>
          <Field label="Last name" isDark={isDark}>
            <TextField fullWidth sx={fieldSx} value={lastName} onChange={(e) => setLastName(sanitizeNameInput(e.target.value))} />
          </Field>
        </Box>
        <Field label="Email" isDark={isDark}>
          <TextField type="email" fullWidth sx={{ mb: 2.5, ...fieldSx }} value={email} onChange={(e) => setEmail(e.target.value)} />
        </Field>
        <Field label="Phone number" isDark={isDark}>
          <TextField type="tel" fullWidth sx={{ mb: 2.5, ...fieldSx }} value={phone} onChange={(e) => setPhone(sanitizePhoneInput(e.target.value))} />
        </Field>

        {/* Quantity + running total, presented as its own card rather than a
            bare row of controls — makes the $ impact the visual focus. */}
        <Box sx={{
          borderRadius: "14px", p: 2.5,
          border: `1px solid ${isDark ? "rgba(255,255,255,0.1)" : "rgba(10,14,39,0.1)"}`,
          bgcolor: isDark ? "rgba(255,255,255,0.03)" : "rgba(10,14,39,0.015)",
        }}>
          <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", color: isDark ? "#94a3b8" : "#64748b", textTransform: "uppercase", mb: 1.5 }}>
            Kits to sponsor
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
            <Stepper value={qty} onChange={setQty} min={1} max={PAY_IT_FORWARD_MAX_QTY} isDark={isDark} size="big" />
            <Box sx={{ textAlign: "right" }}>
              <Typography sx={{ fontSize: 26, fontWeight: 800, color: ROSE, lineHeight: 1 }}>
                ${(qty * KIT_PRICE).toLocaleString()}
              </Typography>
              <Typography sx={{ fontSize: 11.5, color: isDark ? "#64748b" : "#94a3b8", mt: 0.25 }}>
                total
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, pt: 1.25, borderTop: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(10,14,39,0.08)"}` }}>
            <FavoriteIcon sx={{ fontSize: 13, color: ROSE }} />
            <Typography sx={{ fontSize: 12, color: isDark ? "#94a3b8" : "#64748b" }}>
              = {qty} famil{qty === 1 ? "y" : "ies"} tested who couldn't otherwise afford it
            </Typography>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ bgcolor: cardBg, px: 3, pb: 3, pt: 0 }}>
        <Button onClick={onClose} disabled={submitting} sx={{ textTransform: "none", color: isDark ? "#94a3b8" : "#64748b" }}>Cancel</Button>
        <Button
          variant="contained" disabled={!canSubmit || submitting} onClick={handleSubmit}
          startIcon={!submitting ? <FavoriteIcon sx={{ fontSize: 16 }} /> : null}
          sx={{
            textTransform: "none", borderRadius: "10px", px: 3,
            background: `linear-gradient(135deg, #0a0e27 0%, ${ROSE} 140%)`,
            "&:hover": { background: `linear-gradient(135deg, #0a0e27 0%, #be123c 140%)` },
            "&.Mui-disabled": { background: isDark ? "rgba(255,255,255,0.08)" : "rgba(10,14,39,0.12)" },
          }}
        >
          {submitting ? "Starting checkout…" : "Continue to Payment"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
